import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Add,
  Delete,
  Person,
  Phone,
  Lock,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth.js';
import { StaffService } from '../../services/staffService.js';

export const StaffPanel = () => {
  const { outletId } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ phone: '', name: '', pin: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchStaff = useCallback(async () => {
    if (!outletId) return;
    try {
      const data = await StaffService.listStaff(outletId);
      setStaff(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [outletId]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const handleAddStaff = async () => {
    setSubmitting(true);
    setError('');
    try {
      await StaffService.addStaff({
        phone: form.phone,
        name: form.name,
        pin: form.pin,
        outletId,
      });
      setDialogOpen(false);
      setForm({ phone: '', name: '', pin: '' });
      fetchStaff();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveStaff = async (userId) => {
    try {
      await StaffService.removeStaff(userId);
      fetchStaff();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography fontWeight={700} fontSize="1rem">
          Staff Members
        </Typography>
        <Button
          startIcon={<Add />}
          size="small"
          variant="contained"
          onClick={() => setDialogOpen(true)}
          sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '0.78rem' }}
        >
          Add Staff
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {staff.length === 0 ? (
        <Typography color="text.secondary" fontSize="0.85rem">
          No staff members added yet.
        </Typography>
      ) : (
        <List disablePadding>
          {staff.map((member, index) => (
            <React.Fragment key={member.user_id}>
              <ListItem sx={{ px: 0 }}>
                <Person sx={{ mr: 1.5, color: 'text.secondary', fontSize: 20 }} />
                <ListItemText
                  primary={member.name}
                  secondary={member.phone}
                  primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveStaff(member.user_id)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              {index < staff.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}

      {/* Add Staff Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Staff Member</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start"><Person fontSize="small" /></InputAdornment> }}
            />
            <TextField
              fullWidth
              label="Phone (10 digits)"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
              InputProps={{ startAdornment: <InputAdornment position="start"><Phone fontSize="small" /></InputAdornment> }}
            />
            <TextField
              fullWidth
              label="PIN"
              type="password"
              value={form.pin}
              onChange={(e) => setForm({ ...form, pin: e.target.value.slice(0, 6) })}
              InputProps={{ startAdornment: <InputAdornment position="start"><Lock fontSize="small" /></InputAdornment> }}
              helperText="Staff will use this PIN to login"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddStaff}
            disabled={!form.name.trim() || form.phone.length !== 10 || !form.pin || submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
